import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cable,
  Check,
  Copy,
  Globe,
  Key,
  Loader2,
  Package,
  Pause,
  Play,
  RefreshCw,
  RotateCw,
  Webhook,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../shadcn/ui/dialog";
import { Button } from "../../../../shadcn/ui/button";
import { Input } from "../../../../shadcn/ui/input";
import { Badge } from "../../../../shadcn/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { Constants } from "../../../../utils/constants";
import StatusBadge from "./StatusBadge";
import ApiKeySection from "./ApiKeySection";
import StagingReview from "./StagingReview";
import PeerBaseUrlField, { normalizePeerBaseUrl, PEER_ENVIRONMENTS } from "../../../../components/PeerBaseUrlField";
import federationService from "../../../../services/host-apiServices/federationService";
import toastUtils from "../../../../utils/toast";
import { formatTimeAgo } from "../../../../utils/timeAgoUtils";

const WIRE_META = {
  code: { label: "Access code", icon: Key },
  updates: { label: "Live updates", icon: Webhook },
  spaces: { label: "Spaces", icon: Package },
};

const WireRow = ({ wireKey, lit, open, isExpose, onClick }) => {
  const meta = WIRE_META[wireKey];
  const Icon = meta.icon;
  const FlowArrow = isExpose ? ChevronRight : ChevronLeft;
  const lineClass = lit ? "border-solid border-green-400" : "border-dashed border-gray-200";

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 py-2 text-left hover:bg-white/60 rounded-lg transition-colors"
    >
      <div className={`flex-1 border-t-2 ${lineClass}`} />
      <div
        className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 z-10 transition-all ${
          lit ? "bg-white ring-4 ring-green-100" : "bg-white ring-4 ring-transparent"
        }`}
      >
        <Icon className={`w-3.5 h-3.5 ${lit ? "text-green-500" : "text-gray-300"}`} />
      </div>
      <span className={`text-xs font-semibold shrink-0 ${lit ? "text-green-700" : "text-gray-400"}`}>
        {meta.label}
      </span>
      <FlowArrow className={`w-3.5 h-3.5 shrink-0 ${lit ? "text-green-500" : "text-gray-300"}`} />
      <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      <div className={`flex-1 border-t-2 ${lineClass}`} />
    </button>
  );
};

const generateWebhookSecret = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let raw = "";
  for (let i = 0; i < bytes.length; i++) raw += String.fromCharCode(bytes[i]);
  return btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const deriveApiOrigin = appUrl => {
  try {
    const url = new URL(appUrl);
    url.hostname = url.hostname.replace(/^app\./, "api.");
    return url.origin;
  } catch {
    return null;
  }
};

const PartnerKeyCard = ({ binding, onSaved }) => {
  const [accessKey, setAccessKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedKey, setSavedKey] = useState(null);
  const configuredEnv = normalizePeerBaseUrl(binding?.config?.remoteBaseUrl) || "";
  const [envValue, setEnvValue] = useState(configuredEnv);

  const finish = async key => {
    const env = configuredEnv || envValue;
    try {
      if (!configuredEnv && envValue) {
        await federationService.setBindingConfig(binding.id, { remoteBaseUrl: envValue });
      }
      const baseUrl = Constants.INTEGRATIONS_API_BASE_URL.replace("/api/v2/integrations", "");
      const wh = await federationService.getWebhookInfo(binding.id);
      let webhookUrl = `${baseUrl}/integrations/webhooks/${binding.providerCode}/${wh.token}`;
      const origin = deriveApiOrigin(env);
      if (origin) {
        try {
          const parsed = new URL(webhookUrl);
          const target = new URL(origin);
          parsed.protocol = target.protocol;
          parsed.hostname = target.hostname;
          parsed.port = target.port;
          webhookUrl = parsed.toString();
        } catch {}
      }
      await federationService.registerWebhookOnHost(env, key, {
        webhookUrl,
        signingSecret: wh.signingSecret || undefined,
        eventTypes: ["inventory.changed", "property.changed", "booking.changed", "booking.canceled", "system.ping"],
      });
      toastUtils.success("Connected — live updates switched on");
    } catch (err) {
      toastUtils.error(err?.response?.data?.message || "Connected, but live updates need a retry from the Live updates line");
    } finally {
      setSavedKey(null);
      onSaved?.();
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!accessKey.trim()) return;
    setSaving(true);
    const key = accessKey.trim();
    try {
      await federationService.setCredentials(binding.id, {
        secrets: { hostApiKey: key, webhookSigningSecret: generateWebhookSecret() },
      });
      setAccessKey("");
      onSaved?.();
      if (configuredEnv || envValue) {
        await finish(key);
      } else {
        setSavedKey(key);
      }
    } catch (err) {
      toastUtils.error(err?.response?.data?.message || "That code didn't work — check it with your partner");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
          <Key className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">Enter your partner's access code</p>
          <p className="text-xs text-gray-500 mt-0.5">They'll find it in their Spacia integration.</p>
        </div>
      </div>
      <Input
        type="password"
        value={accessKey}
        onChange={e => setAccessKey(e.target.value)}
        placeholder="Paste access code"
        autoFocus
      />

      {savedKey && !configuredEnv && (
        <div className="space-y-2 pt-1 border-t border-gray-200/70">
          <label className="block text-xs font-medium text-gray-700">Which Spacia environment are they on?</label>
          <Select value={envValue} onValueChange={setEnvValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select environment" />
            </SelectTrigger>
            <SelectContent>
              {PEER_ENVIRONMENTS.map(en => (
                <SelectItem key={en.id} value={en.value}>
                  {en.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {savedKey && !configuredEnv ? (
        <Button type="button" disabled={!envValue || saving} onClick={() => finish(savedKey)} className="w-full">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Finish connecting
        </Button>
      ) : (
        <Button type="submit" disabled={saving || !accessKey.trim()} className="w-full">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {saving ? "Connecting…" : savedKey ? "Reconnect" : "Continue"}
        </Button>
      )}
    </form>
  );
};

const WireUpdatesPanel = ({ binding, bindingId }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newSecret, setNewSecret] = useState("");
  const [peerKey, setPeerKey] = useState("");
  const [savingSecret, setSavingSecret] = useState(false);
  const isExpose = binding?.direction === "EXPOSE";

  useEffect(() => {
    let cancelled = false;
    federationService
      .getWebhookInfo(bindingId)
      .then(data => {
        if (!cancelled) setInfo(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bindingId]);

  const handleRotate = async () => {
    if (
      info?.signingSecret &&
      !window.confirm("Rotate the webhook signing secret? The old one stops working immediately.")
    )
      return;
    setRotating(true);
    try {
      await federationService.rotateWebhookSecret(bindingId);
      toastUtils.success("Signing secret rotated");
      setInfo(await federationService.getWebhookInfo(bindingId));
    } catch {
      toastUtils.error("Failed to rotate secret");
    } finally {
      setRotating(false);
    }
  };

  if (loading) return <div className="h-16 bg-gray-100 rounded-xl animate-pulse" />;

  const handleSaveSecret = async () => {
    const env = normalizePeerBaseUrl(binding?.config?.remoteBaseUrl);
    if (!env) {
      toastUtils.error("Set your partner's environment first (Advanced settings)");
      return;
    }
    setSavingSecret(true);
    try {
      const secrets = { hostApiKey: peerKey.trim() };
      if (newSecret.trim()) secrets.webhookSigningSecret = newSecret.trim();
      await federationService.setCredentials(bindingId, { secrets });
      await federationService.registerWebhookOnHost(env, peerKey.trim(), {
        webhookUrl: info.inboundUrl,
        signingSecret: newSecret.trim() || undefined,
        eventTypes: ["inventory.changed", "property.changed", "booking.changed", "booking.canceled", "system.ping"],
      });
      toastUtils.success("Signing secret updated and shared with your partner");
      setNewSecret("");
      setPeerKey("");
    } catch (err) {
      toastUtils.error(err?.response?.data?.message || "Couldn't apply — check the access code and environment");
    } finally {
      setSavingSecret(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">
      <div>
        <p className="text-sm font-semibold text-gray-900">Live updates</p>
        {isExpose ? (
          <p className="text-xs text-gray-500 mt-0.5">
            Once your partner connects with your access code, every change you make streams to them automatically.
          </p>
        ) : !binding.credentialsSet ? (
          <p className="text-xs text-gray-500 mt-0.5">
            Switches on automatically once the access code is connected — nothing to do here yet.
          </p>
        ) : info?.inboundUrl ? (
          <>
            <p className="text-xs font-semibold text-gray-700 mt-1">Step 1 — Where updates arrive</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Your private receiving address — handled automatically, no action needed:
            </p>
            <div className="flex items-start gap-2 mt-2">
              <code className="flex-1 min-w-0 px-3 py-1.5 text-xs bg-white border border-gray-200 rounded font-mono break-all leading-relaxed">
                {info.inboundUrl}
              </code>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(info.inboundUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="p-1.5 text-gray-400 hover:text-gray-700 shrink-0"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>

            <p className="text-xs font-semibold text-gray-700 mt-4">Step 2 — Signing secret</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Stamps every incoming update so forged ones are rejected. Connecting through this screen sets one up
              automatically — its value stays hidden. Set one here only if updates fail verification or you need a
              specific value.
            </p>
            <div className="flex gap-2 mt-2">
              <Input
                type="password"
                value={newSecret}
                onChange={e => setNewSecret(e.target.value)}
                placeholder="Leave blank to keep current"
                className="flex-1 bg-white"
              />
              <button
                type="button"
                onClick={() => setNewSecret(generateWebhookSecret())}
                className="px-2.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 rounded-lg shrink-0"
              >
                Generate
              </button>
            </div>
            <div className="mt-2">
              <Input
                type="password"
                value={peerKey}
                onChange={e => setPeerKey(e.target.value)}
                placeholder="Partner's access code (required to apply)"
                className="bg-white"
              />
            </div>
            <div className="flex justify-end mt-3">
              <Button
                size="sm"
                onClick={handleSaveSecret}
                disabled={savingSecret || !peerKey.trim()}
              >
                {savingSecret ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Apply to partner
              </Button>
            </div>
          </>
        ) : (
          <p className="text-xs text-gray-400 italic mt-0.5">Not provisioned yet.</p>
        )}
      </div>
      {!isExpose && binding.credentialsSet && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleRotate}
            disabled={rotating}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50"
          >
            <RotateCw className={`w-3 h-3 ${rotating ? "animate-spin" : ""}`} />
            Rotate secret
          </button>
        </div>
      )}
    </div>
  );
};

const ConnectionEndpointCard = ({ binding, onSaved }) => {
  const [showConfig, setShowConfig] = useState(false);
  const [remoteBaseUrl, setRemoteBaseUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRemoteBaseUrl(binding?.config?.remoteBaseUrl || "");
  }, [binding]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await federationService.setBindingConfig(binding.id, { remoteBaseUrl });
      toastUtils.success("Connection settings saved");
      setShowConfig(false);
      onSaved?.();
    } catch (err) {
      toastUtils.error(err?.response?.data?.message || "Failed to save connection settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-slate-100 rounded-2xl bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">Partner's environment</h4>
          <p className="text-xs text-slate-400 mt-0.5">Which Spacia environment your partner runs on.</p>
        </div>
        <button
          type="button"
          onClick={() => setShowConfig(!showConfig)}
          className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-md"
        >
          {showConfig ? "Hide" : "Change"}
        </button>
      </div>

      {showConfig ? (
        <div className="space-y-3 pt-2 border-t border-slate-100">
          <PeerBaseUrlField value={normalizePeerBaseUrl(remoteBaseUrl)} onChange={setRemoteBaseUrl} />
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setShowConfig(false)}
              className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <code className="block text-xs px-2.5 py-1.5 bg-slate-100 rounded-lg break-all font-mono text-slate-600">
          {binding?.config?.remoteBaseUrl || "Not selected yet"}
        </code>
      )}
    </div>
  );
};

const BindingFlowDialog = ({ bindingId, open, onClose, knownDirection }) => {
  const [binding, setBinding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState(null);
  const [activating, setActivating] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [openSection, setOpenSection] = useState(null);
  const [justActivated, setJustActivated] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [currentView, setCurrentView] = useState("auto"); // "wires" | "review" | "auto"
  const prevStatusRef = useRef(null);

  const handleUpdate = useCallback(async () => {
    if (!bindingId) return;
    try {
      const data = await federationService.getBinding(bindingId);
      setBinding({ ...data, direction: data.direction || knownDirection });
    } catch {
      setBinding(null);
    } finally {
      setLoading(false);
    }
  }, [bindingId, knownDirection]);

  const fetchApiKeys = useCallback(async () => {
    if (!bindingId) return;
    try {
      const keys = await federationService.listApiKeys(bindingId);
      setApiKeys(Array.isArray(keys) ? keys : []);
    } catch {
      setApiKeys([]);
    }
  }, [bindingId]);

  useEffect(() => {
    if (!open || !bindingId) return;
    setLoading(true);
    prevStatusRef.current = null;
    setJustActivated(false);
    handleUpdate();
    fetchApiKeys();
  }, [open, bindingId, handleUpdate, fetchApiKeys]);

  useEffect(() => {
    if (!open || !binding || binding.status !== "ACTIVATING") return;
    const timer = setTimeout(() => handleUpdate(), 4000);
    return () => clearTimeout(timer);
  }, [open, binding, handleUpdate]);

  useEffect(() => {
    if (!binding) return;
    if (binding.direction === "CONSUME" && binding.status === "ACTIVE" && prevStatusRef.current === "ACTIVATING") {
      setJustActivated(true);
    }
    prevStatusRef.current = binding.status;
  }, [binding]);

  useEffect(() => {
    if (!binding) return;
    setOpenSection(prev => {
      if (prev) return prev;
      return !binding.credentialsSet ? "code" : "spaces";
    });
  }, [binding]);

  const toggleSection = key => setOpenSection(prev => (prev === key ? null : key));

  const handleTest = async () => {
    setTesting(true);
    setTestError(null);
    try {
      await federationService.testBinding(bindingId);
      await handleUpdate();
    } catch (err) {
      setTestError(err?.response?.data?.message || "Test failed — check credentials and try again");
    } finally {
      setTesting(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      await federationService.activateBinding(bindingId);
      await handleUpdate();
    } catch (err) {
      toastUtils.error(err?.response?.data?.message || "Activation failed");
    } finally {
      setActivating(false);
    }
  };

  const handlePause = async () => {
    setPausing(true);
    try {
      await federationService.pauseBinding(bindingId);
      await handleUpdate();
      toastUtils.success("Integration paused");
    } catch {
      toastUtils.error("Failed to pause");
    } finally {
      setPausing(false);
    }
  };

  if (!open) return null;

  const isExpose = binding?.direction === "EXPOSE";
  const connected = !!binding?.credentialsSet;
  const live = ["ACTIVE", "PAUSED"].includes(binding?.status);
  const latestKey = apiKeys.length > 0 ? apiKeys[apiKeys.length - 1] : null;
  const envLabel =
    PEER_ENVIRONMENTS.find(env => env.value === normalizePeerBaseUrl(binding?.config?.remoteBaseUrl))?.label || null;

  // ── Accordion panels, one per wire ──
  const renderPanel = key => {
    if (key === "code") {
      if (isExpose) {
        return (
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">
            {connected ? (
              <>
                <p className="text-sm font-semibold text-gray-900">Access code created</p>
                <p className="text-xs text-gray-500">
                  {latestKey
                    ? `Send it to your partner — it starts with ${latestKey.prefix}…`
                    : "Send it to your partner so they can connect."}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-900">No access code yet</p>
                <p className="text-xs text-gray-500">Create one in Advanced settings below and send it over.</p>
                <Button variant="outline" size="sm" onClick={() => setShowAdvanced(true)}>
                  Open advanced settings
                </Button>
              </>
            )}
          </div>
        );
      }
      return <PartnerKeyCard binding={binding} onSaved={handleUpdate} />;
    }

    if (key === "updates") {
      return <WireUpdatesPanel binding={binding} bindingId={bindingId} />;
    }

    if (key === "spaces") {
      return (
        <div className="space-y-3">
          {!isExpose && !connected && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
              <p className="text-xs font-semibold text-amber-900">Connect the access code first</p>
              <p className="text-xs text-amber-700">
                Spaces can't start flowing until the code is set — activation would stay stuck.
              </p>
              <Button variant="outline" size="sm" onClick={() => setOpenSection("code")}>
                Go to access code
              </Button>
            </div>
          )}

          {!isExpose && binding.status === "ACTIVATING" && (
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 flex items-start gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-indigo-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {connected ? "Activation in progress…" : "Waiting for the access code"}
                </p>
                <p className="text-xs text-gray-500">
                  {connected
                    ? "This usually completes within a few seconds."
                    : "Set the code above — status won't go live without it."}
                </p>
              </div>
              {connected && (
                <Button size="sm" onClick={handleUpdate}>
                  Refresh
                </Button>
              )}
            </div>
          )}

          {(isExpose ? true : connected) && binding.status !== "ACTIVATING" && (
            <div className="flex gap-2">
              {binding.status === "PENDING" && (
                <Button onClick={handleActivate} disabled={activating} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                  {activating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Activate
                </Button>
              )}
              {binding.status === "PAUSED" && (
                <Button onClick={handleActivate} disabled={activating} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                  {activating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Resume
                </Button>
              )}
              {binding.status === "ACTIVE" && (
                <Button onClick={handlePause} variant="outline" disabled={pausing} className="flex-1">
                  <Pause className="w-4 h-4 mr-2" />
                  Pause
                </Button>
              )}
            </div>
          )}

          {isExpose ? (
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                  <Globe className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Shared with</p>
                  {envLabel ? (
                    <p className="text-sm font-medium text-gray-700 mt-0.5">{envLabel}</p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(true)}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 mt-0.5"
                    >
                      Select an environment in Advanced settings
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            (binding.status === "ACTIVATING" || binding.status === "ACTIVE" || binding.status === "PAUSED") && (
              <div className="p-4 rounded-xl border border-indigo-150 bg-gradient-to-r from-indigo-50/70 via-blue-50/40 to-slate-50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-2xs">
                <div>
                  <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <Package className="w-4 h-4 text-indigo-600" />
                    Inventory Review Studio
                  </h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Review and verify staged buildings and spaces in a clean, spacious view.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setCurrentView("review")}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shrink-0 shadow-sm"
                >
                  Open Review Studio →
                </Button>
              </div>
            )
          )}
        </div>
      );
    }

    return null;
  };

  // Determine if we show the full review canvas or the connection setup wires
  const isReviewActive =
    !isExpose &&
    (binding?.status === "ACTIVATING" || binding?.status === "ACTIVE" || binding?.status === "PAUSED") &&
    (currentView === "review" || (currentView === "auto" && connected));

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent
        className={`max-w-none rounded-2xl flex flex-col p-0 gap-0 overflow-hidden transition-all duration-200 ${
          isReviewActive
            ? "w-[96vw] max-w-[1260px] h-[92vh] max-h-[92vh]"
            : "w-[92vw] sm:max-w-[620px] max-h-[88vh]"
        }`}
      >
        {loading || !binding ? (
          <div className="flex justify-center py-16 items-center">
            <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : isReviewActive ? (
          /* ── FULL STEPPED REVIEW CANVAS (Spacious, distraction-free) ── */
          <div className="flex flex-col flex-1 min-h-0 h-full overflow-hidden bg-white">
            {/* Slim, elegant top status bar */}
            <div className="px-5 py-3 border-b border-slate-200/80 bg-slate-50/70 flex items-center justify-between gap-4 shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  type="button"
                  onClick={() => setCurrentView("wires")}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:text-slate-900 hover:bg-slate-100 shadow-2xs transition-colors shrink-0"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  Connection Settings
                </button>
                <div className="h-4 w-px bg-slate-200 hidden sm:block" />
                <div className="flex items-center gap-2 truncate">
                  <span className="text-xs font-bold text-slate-900 capitalize">
                    {binding.providerCode || "Partner"}
                  </span>
                  <StatusBadge status={binding.status} />
                  <span className="text-xs text-slate-400 hidden md:inline">· Inventory Review & Sync</span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {binding.status === "ACTIVE" && (
                  <Button onClick={handlePause} variant="outline" size="sm" disabled={pausing} className="h-7 text-xs">
                    <Pause className="w-3 h-3 mr-1" />
                    Pause
                  </Button>
                )}
                {binding.status === "PAUSED" && (
                  <Button onClick={handleActivate} size="sm" disabled={activating} className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white">
                    <Play className="w-3 h-3 mr-1" />
                    Resume
                  </Button>
                )}
                <Button size="sm" onClick={onClose} className="h-7 text-xs">
                  Done
                </Button>
              </div>
            </div>

            {/* Main review studio */}
            <div className="flex-1 overflow-y-auto min-h-0 p-3 sm:p-5 bg-slate-100/40">
              <StagingReview
                bindingId={bindingId}
                direction="CONSUME"
                justActivated={justActivated}
                onBackToWires={() => setCurrentView("wires")}
              />
            </div>
          </div>
        ) : (
          /* ── CONNECTION SETUP VIEW (Wires, simple and focused) ── */
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <DialogHeader>
                <DialogTitle>
                  {isExpose ? "Share" : "Connect"} — {binding.providerCode}
                </DialogTitle>
              </DialogHeader>

              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="bg-indigo-100 text-indigo-700 text-xs">
                  {isExpose ? "Sharing" : "Connecting"}
                </Badge>
                <StatusBadge status={binding.status} />
              </div>

              {/* The three channels */}
              <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-3">
                <div className="flex items-center justify-between px-2 pb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-white border border-slate-100 shadow-sm flex items-center justify-center overflow-hidden">
                      <img
                        src="/assets/img/new-logo.svg"
                        alt=""
                        className="w-5 h-5 object-contain"
                        onError={e => (e.target.style.display = "none")}
                      />
                    </div>
                    <span className="text-xs font-bold text-slate-700">You</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 capitalize">{binding.providerCode || "Partner"}</span>
                    <div className="w-8 h-8 rounded-lg bg-white border border-slate-100 shadow-sm flex items-center justify-center">
                      <Cable className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                </div>

                <WireRow
                  wireKey="code"
                  lit={connected}
                  open={openSection === "code"}
                  isExpose={isExpose}
                  onClick={() => toggleSection("code")}
                />
                {openSection === "code" && <div className="px-1 pb-2">{renderPanel("code")}</div>}

                <WireRow
                  wireKey="updates"
                  lit={live}
                  open={openSection === "updates"}
                  isExpose={isExpose}
                  onClick={() => toggleSection("updates")}
                />
                {openSection === "updates" && <div className="px-1 pb-2">{renderPanel("updates")}</div>}

                <WireRow
                  wireKey="spaces"
                  lit={live}
                  open={openSection === "spaces"}
                  isExpose={isExpose}
                  onClick={() => toggleSection("spaces")}
                />
                {openSection === "spaces" && <div className="px-1 pb-2">{renderPanel("spaces")}</div>}
              </div>

              {/* Advanced settings */}
              <div className="pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-800"
                >
                  {showAdvanced ? "Hide advanced settings" : "Advanced settings & keys"}
                </button>

                {showAdvanced && (
                  <div className="space-y-4 mt-3">
                    {!isExpose && <ConnectionEndpointCard binding={binding} onSaved={handleUpdate} />}
                    {isExpose && (
                      <div className="space-y-2">
                        <p className="text-xs text-slate-400">
                          These are the codes your partner pastes into their integration. Create a new key only to
                          replace one that's expiring or compromised — the old one stops working after a short grace
                          period.
                        </p>
                        <ApiKeySection
                          bindingId={bindingId}
                          capabilities={binding.capabilitiesEnabled?.split(",").map(c => c.trim()) || []}
                          onKeyChange={fetchApiKeys}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Fixed footer connection bar — test from anywhere */}
            <div className="px-6 py-3 bg-white border-t border-slate-100 flex items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <p className={`text-xs truncate ${testError ? "text-red-600 font-medium" : "text-slate-400"}`} title={testError || undefined}>
                  {testing
                    ? "Testing connection…"
                    : testError
                      ? testError
                      : binding.lastHealthCheckAt
                        ? `Last checked ${formatTimeAgo(new Date(binding.lastHealthCheckAt))}`
                        : ""}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={handleTest} disabled={testing}>
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${testing ? "animate-spin" : ""}`} />
                  Test
                </Button>
                <Button size="sm" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
export default BindingFlowDialog;
